import { getCurrencyFormatter } from "@redotech/money/util";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import CheckIcon from "@redotech/redo-icons/check.svg";
import ChevronDown from "@redotech/redo-icons/chevron-down.svg";
import DotIcon from "@redotech/redo-icons/dot-04.svg";
import ImageIcon from "@redotech/redo-icons/image-01.svg";
import LinkExternalIcon from "@redotech/redo-icons/link-external-02.svg";
import PackageIcon from "@redotech/redo-icons/package.svg";
import RefreshIcon from "@redotech/redo-icons/refresh-cw-02.svg";
import StatusIcon from "@redotech/redo-icons/status.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { FulfillmentGroupPickStatus } from "@redotech/redo-model/fulfillments/fulfillment-group";
import {
  isRedoLineItem,
  type Fulfillment,
  type LineItem,
} from "@redotech/redo-model/order";
import type { ShippingLine } from "@redotech/redo-model/shopify-order";
import {
  RedoBadge,
  RedoBadgeColor,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { CopyableText } from "@redotech/redo-web/copyable-text";
import { Flex } from "@redotech/redo-web/flex";
import { Icon } from "@redotech/redo-web/icon";
import { Text } from "@redotech/redo-web/text";
import { unique } from "@redotech/util/array";
import { getDateString } from "@redotech/util/date-format";
import { formatSentenceCase } from "@redotech/util/string";
import { assertNever } from "@redotech/util/type";
import classNames from "classnames";
import { memo, useState } from "react";
import { formatShippingAddressString } from "../../order/format-shipping-address";
import { orderExternalUrl } from "../../return/util";
import type { OrderWithPickStatus } from "../db/collections";
import { FieldLabel } from "./field-label";
import { OrderCardActions } from "./order-card-actions";
import * as styles from "./shared-card-components.module.css";

export interface OrderCardProps {
  order: OrderWithPickStatus;
}

export const OrderCard = memo(function OrderCard({ order }: OrderCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const team = useRequiredContext(TeamContext);

  const handleToggle = useHandler(() => {
    setIsExpanded((prev) => !prev);
  });

  const orderName = order.shopify.name;
  const orderDate = new Date(order.shopifyCreatedAt);
  const totalPrice = order.shopify.total_price_usd || order.shopify.total_price;
  const currency = order.shopify.presentment_currency;
  const externalUrl = orderExternalUrl(team.storeUrl, order);

  return (
    <Flex
      bgColor="secondary"
      borderColor="secondary"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="md"
      p="md"
      radius="md"
    >
      <Flex align="center" gap="xs" justify="space-between">
        <Flex align="center" gap="xxs" justify="center">
          <CopyableText
            altTextToCopy={
              orderName.startsWith("#") ? orderName.slice(1) : orderName
            }
            fontSize="xs"
            fontWeight="medium"
          >
            {`Order ${orderName}`}
          </CopyableText>
          {externalUrl && (
            <RedoButton
              hierarchy="tertiary"
              IconLeading={LinkExternalIcon}
              onClick={() => {
                window.open(externalUrl, "_blank");
              }}
              size="xs"
            />
          )}
        </Flex>

        <Flex align="center" gap="xxs">
          <OrderCardActions order={order} />
          <Flex
            className={classNames(
              styles.chevron,
              isExpanded && styles.chevronRotated,
            )}
            onClick={handleToggle}
          >
            <Icon
              arbiterIconSvg={ChevronDown}
              color="ghost"
              size="extra-small"
            />
          </Flex>
        </Flex>
      </Flex>

      <OrderBadges order={order} />

      <Flex dir="column" gap="md">
        <Flex align="center" gap="md" justify="space-between">
          <FieldLabel>Ordered on</FieldLabel>
          <CopyableText fontSize="xs" fontWeight="regular" textColor="primary">
            {getDateString(orderDate)}
          </CopyableText>
        </Flex>

        <Flex align="center" gap="md" justify="space-between">
          <FieldLabel>Total</FieldLabel>
          <CopyableText fontSize="xs" fontWeight="regular" textColor="primary">
            {getCurrencyFormatter(currency).format(totalPrice)}
          </CopyableText>
        </Flex>
      </Flex>

      {isExpanded && (
        <Flex dir="column" gap="md">
          <OrderShippingMethod order={order} />

          <OrderShippingAddress order={order} />

          <OrderFulfillmentDetails order={order} />

          {order.shopify.line_items.some((lineItem) =>
            isRedoLineItem(lineItem),
          ) && (
            <>
              <FieldLabel>Coverage</FieldLabel>
              {order.shopify.line_items
                .filter((lineItem) => isRedoLineItem(lineItem))
                .map((lineItem) => (
                  <OrderLineItem
                    currency={currency}
                    key={lineItem.id}
                    lineItem={lineItem}
                  />
                ))}
            </>
          )}

          {order.shopify.line_items.some(
            (lineItem) => !isRedoLineItem(lineItem),
          ) && (
            <>
              <FieldLabel>Item(s)</FieldLabel>
              {order.shopify.line_items
                .filter((lineItem) => !isRedoLineItem(lineItem))
                .map((lineItem) => (
                  <OrderLineItem
                    currency={currency}
                    key={lineItem.id}
                    lineItem={lineItem}
                  />
                ))}
            </>
          )}
        </Flex>
      )}
    </Flex>
  );
});

const getPaymentStatusColor = (status: string): RedoBadgeColor => {
  switch (status) {
    case "paid":
      return "success";
    case "partially_refunded":
      return "warning";
    case "refunded":
      return "error";
    default:
      return "gray";
  }
};

const getPaymentStatusIcon = (
  status: string,
): { Icon: typeof DotIcon; size?: "xs" } => {
  switch (status) {
    case "paid":
      return { Icon: CheckIcon };
    case "partially_refunded":
      return { Icon: StatusIcon };
    case "refunded":
      return { Icon: RefreshIcon };
    default:
      return { Icon: DotIcon, size: "xs" };
  }
};

const getPickStatusBadge = (
  status: FulfillmentGroupPickStatus,
): { label: string; color: RedoBadgeColor } => {
  switch (status) {
    case FulfillmentGroupPickStatus.PROBLEM:
      return { label: "Picking problem", color: "error" };
    case FulfillmentGroupPickStatus.IN_PROGRESS:
      return { label: "Picking", color: "warning" };
    case FulfillmentGroupPickStatus.PICKED:
      return { label: "Picked", color: "success" };
    default:
      return assertNever(status);
  }
};

const OrderBadges = memo(function OrderBadges({
  order,
}: {
  order: OrderWithPickStatus;
}) {
  const pickStatuses = order.pickStatuses || [];
  const uniquePickStatuses = Array.from(new Set(pickStatuses));

  const isUnfulfilled = !order.shopify?.fulfillment_status;
  const hasPickStatuses = uniquePickStatuses.length > 0;

  // Only show "Unfulfilled" badge if the order is unfulfilled AND all pick statuses are PICKED
  // Otherwise, the picking status (like "Picking" or "Picking problem") already implies unfulfilled
  const allPicked =
    hasPickStatuses &&
    uniquePickStatuses.every(
      (status) => status === FulfillmentGroupPickStatus.PICKED,
    );
  const shouldShowUnfulfilledBadge = isUnfulfilled && allPicked;

  return (
    <Flex gap="xs" wrap="wrap">
      {order.shopify?.financial_status && (
        <RedoBadge
          color={getPaymentStatusColor(order.shopify.financial_status)}
          segmentLeading={{
            type: "icon",
            ...getPaymentStatusIcon(order.shopify.financial_status),
          }}
          size="sm"
          text={formatSentenceCase(order.shopify.financial_status)}
        />
      )}
      {order.shopify?.fulfillment_status && (
        <RedoBadge
          color={
            order.shopify.fulfillment_status === "fulfilled"
              ? "success"
              : "warning"
          }
          segmentLeading={{
            type: "icon",
            Icon:
              order.shopify.fulfillment_status === "fulfilled"
                ? CheckIcon
                : PackageIcon,
          }}
          size="sm"
          text={formatSentenceCase(order.shopify.fulfillment_status)}
        />
      )}
      {shouldShowUnfulfilledBadge && (
        <RedoBadge
          color="warning"
          segmentLeading={{ type: "icon", Icon: PackageIcon }}
          size="sm"
          text="Unfulfilled"
        />
      )}
      {order.shopify?.cancelled_at && (
        <RedoBadge
          color="error"
          segmentLeading={{ type: "icon", Icon: XCloseIcon }}
          size="sm"
          text="Canceled"
        />
      )}
      {order.protected && (
        <RedoBadge color="blueLight" size="sm" text="Returns Coverage" />
      )}
      {order.packageProtected && (
        <RedoBadge color="blueLight" size="sm" text="Package Protection" />
      )}
      {hasPickStatuses &&
        uniquePickStatuses.map((status) => {
          const badge = getPickStatusBadge(status);
          return (
            <RedoBadge
              color={badge.color}
              key={status}
              segmentLeading={{ type: "icon", Icon: PackageIcon }}
              size="sm"
              text={badge.label}
            />
          );
        })}
    </Flex>
  );
});

const OrderShippingMethod = memo(function OrderShippingMethod({
  order,
}: {
  order: OrderWithPickStatus;
}) {
  const shippingLines = order.shopify?.shipping_lines || [];
  if (shippingLines.length === 0) {
    return null;
  }

  const uniqueShippingLines = unique(
    shippingLines,
    (line: ShippingLine) => line.title,
  );

  return (
    <Flex align="center" gap="md" justify="space-between">
      <FieldLabel>
        {uniqueShippingLines.length === 1
          ? "Shipping method"
          : "Shipping methods"}
      </FieldLabel>
      <CopyableText fontSize="xs" fontWeight="regular" textColor="primary">
        {uniqueShippingLines.map((line) => line.title).join(", ")}
      </CopyableText>
    </Flex>
  );
});

const OrderShippingAddress = memo(function OrderShippingAddress({
  order,
}: {
  order: OrderWithPickStatus;
}) {
  const shippingAddress = formatShippingAddressString(
    order.shopify.shipping_address
      ? { ...order.shopify.shipping_address, name: null }
      : null,
  );

  if (!shippingAddress) {
    return null;
  }

  return (
    <Flex align="flex-start" gap="md" justify="space-between">
      <FieldLabel>Shipping address</FieldLabel>
      <Flex className={styles.shippingAddress}>
        <CopyableText fontSize="xs" textColor="primary">
          {shippingAddress}
        </CopyableText>
      </Flex>
    </Flex>
  );
});

const getShipmentStatusBadgeColor = (
  status: string | null | undefined,
): RedoBadgeColor => {
  if (!status) {
    return "warning";
  }

  const lowerStatus = status.toLowerCase();
  if (lowerStatus === "delivered" || lowerStatus === "fulfilled") {
    return "success";
  }
  if (lowerStatus === "shipped" || lowerStatus === "in_transit") {
    return "blueLight";
  }
  return "warning";
};

const OrderFulfillmentDetails = memo(function OrderFulfillmentDetails({
  order,
}: {
  order: OrderWithPickStatus;
}) {
  const fulfillments = order.shopify?.fulfillments || [];

  if (fulfillments.length === 0) {
    return null;
  }

  return (
    <Flex dir="column" gap="md">
      {fulfillments.map((fulfillment: Fulfillment, index: number) => (
        <Flex dir="column" gap="md" key={index}>
          {fulfillments.length > 1 && (
            <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
              Package {index + 1}
            </Text>
          )}

          {fulfillment.shipment_status && (
            <Flex>
              <RedoBadge
                color={getShipmentStatusBadgeColor(fulfillment.shipment_status)}
                size="sm"
                text={formatSentenceCase(fulfillment.shipment_status)}
              />
            </Flex>
          )}

          {fulfillment.tracking_number && (
            <Flex align="center" gap="md" justify="space-between">
              <FieldLabel>Tracking</FieldLabel>
              <Flex align="center" gap="xs">
                <CopyableText fontSize="xs" textColor="primary">
                  {fulfillment.tracking_number}
                </CopyableText>
                {fulfillment.tracking_url && (
                  <RedoButton
                    hierarchy="tertiary"
                    IconLeading={LinkExternalIcon}
                    onClick={() => {
                      window.open(
                        fulfillment.tracking_url ?? undefined,
                        "_blank",
                      );
                    }}
                    size="xs"
                  />
                )}
              </Flex>
            </Flex>
          )}
        </Flex>
      ))}
    </Flex>
  );
});

const OrderLineItem = memo(function OrderLineItem({
  lineItem,
  currency,
}: {
  lineItem: LineItem;
  currency: string;
}) {
  return (
    <Flex align="flex-start" gap="sm">
      <Flex
        align="center"
        bgColor={lineItem.image?.src ? undefined : "tertiary"}
        borderColor="secondary"
        borderStyle="solid"
        borderWidth="1px"
        className={styles.imageContainer}
        justify="center"
        radius="sm"
        shrink="0"
      >
        {lineItem.image?.src ? (
          <img
            alt={lineItem.name}
            className={styles.image}
            src={lineItem.image.src}
          />
        ) : (
          <Icon arbiterIconSvg={ImageIcon} color="ghost" size="compact" />
        )}
      </Flex>

      <Flex dir="column" gap="none" grow="1">
        <Text fontSize="xs" fontWeight="regular" textColor="primary">
          {getCurrencyFormatter(currency).format(Number(lineItem.price))}
        </Text>
        <Text fontSize="xs" fontWeight="regular" textColor="primary">
          {lineItem.name}
        </Text>
        {lineItem.variant_title && (
          <Text fontSize="xs" textColor="tertiary">
            {lineItem.variant_title}
          </Text>
        )}
      </Flex>
    </Flex>
  );
});
